import useAction from '@hooks/useAction';
import { Button, type ButtonProps } from '@mantine/core';
import { useHandleError } from '@util/errors';
import { createScope } from 'cross-state';
import { type ComponentPropsWithoutRef } from 'react';

export interface ActionButtonProps extends ButtonProps, Omit<ComponentPropsWithoutRef<'button'>, keyof ButtonProps> {
  onClick?(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void | Promise<void>;
  sticky?: boolean;
}

const ActionButtonScope = createScope(0);

export function ActionButton({ onClick = () => undefined, sticky, ...props }: ActionButtonProps) {
  const scope = ActionButtonScope.useScope();
  const anyInProgress = scope.useStore((x) => x > 0);

  const { execute, inProgress, error } = useAction(onClick);
  useHandleError(error);

  const button = (
    <Button
      {...props}
      disabled={anyInProgress || props.disabled}
      loading={inProgress || props.loading}
      onClick={execute}
    />
  );

  if (sticky !== undefined) {
    return (
      <div
        css={{
          position: 'sticky',
          bottom: sticky ? 'var(--app-shell-footer-offset)' : '-1000px',
          transition: 'all 500ms ease',
          margin: 'calc(0px - var(--mantine-spacing-md))',
          padding: 'var(--mantine-spacing-md)',
          background: sticky ? 'var(--mantine-color-body)' : 'transparent',
          display: 'grid',
          zIndex: 1,
        }}
      >
        {button}
      </div>
    );
  }

  return button;
}
